import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, Fade, IconButton, Tab, Tabs } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { listCaptures } from "../../../api/captures";
import { calculateTimflowSteady, downloadTimflowSteadyCsv, getCalculationByCaptureId } from "../../../api/methods";
import { FLOW_TEST_METHODS_CONFIG } from "../../../config/Methods.config";
import { VULNERABILITY_METHODS_CONFIG } from "../../../config/VulnerabilityMethods.config";
import {
  useCaptureLithologies,
  useCaptureMethodsInputs,
  useCapturesMethodsInputs,
  useCaptureVulnerability,
} from "../../../hooks/Capture";
import { Method, TimflowSteadyResult } from "../../../types/interfaces/methods";
import { TimflowSteadyRequest } from "../../../types/model/calculation";
import { resolveMethodsInputError } from "../../../utils/Methods/methodsInputsErrors";
import CaptureTimflowSteady from "../../Methods/CaptureTimflowSteady";
import MethodRunner from "../../Methods/MethodRunner/Method";
import MethodsInputs from "../../MethodsInput/MethodsInputs";
import CaptureDrasticEppna from "../../Vulnerability/DrasticEppna/CaptureDrasticEppna";
import CaptureGodAvi from "../../Vulnerability/GodAvi/CaptureGodAvi";
import { createMethodsInput as createMethodsInputApi } from "../../../api/methodsInput";
import { CreateMethodsInputRequest } from "../../../types/model/methodsInput";

interface CalculationsTabProps {
  captureId: string;
  subIndex: number;
  handleError: (error: any) => void;
  canEdit: boolean;
}

const CalculationsTab: React.FC<CalculationsTabProps> = ({
  captureId,
  subIndex,
  handleError,
  canEdit,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [methodsTabIndex, setMethodsTabIndex] = useState(0);
  const [vulnerabilityTabIndex, setVulnerabilityTabIndex] = useState(0);
  const [timflowResult, setTimflowResult] = useState<TimflowSteadyResult | null>(null);
  const [isCalculatingTimflow, setIsCalculatingTimflow] = useState(false);
  const [isDownloadingTimflow, setIsDownloadingTimflow] = useState(false);
  const [timflowCaptureIds, setTimflowCaptureIds] = useState<number[]>([
    Number(captureId),
  ]);
  const { data: capturesPage } = useQuery({
    queryKey: ["captures", "timflow-wells"],
    queryFn: () =>
      listCaptures(undefined, undefined, undefined, undefined, 1000, 0, true),
  });
  const captures = capturesPage?.data ?? [];
  const { methodsInputsByCapture, isPending: isPendingTimflowInputs } =
    useCapturesMethodsInputs(timflowCaptureIds);

  const {
    methodsInputsData,
    isPending: isPendingInputs,
    createMethodsInput,
    updateMethodsInput,
    deleteMethodsInput,
  } = useCaptureMethodsInputs(captureId);

  const {
    lithologiesData,
    createLithology,
    updateLithology,
    deleteLithology,
    refetch,
  } = useCaptureLithologies(captureId);

  const activeVulnerabilityMethod =
    VULNERABILITY_METHODS_CONFIG[vulnerabilityTabIndex]?.method;
  const {
    resultsData: vulnerabilityResults,
    calculate: calculateVulnerability,
    isPending: isPendingVulnerability,
  } = useCaptureVulnerability(captureId, activeVulnerabilityMethod);

  const timflowTabIndex = FLOW_TEST_METHODS_CONFIG.length;

  const handleTimflowCalculate = async (
    routeCaptureId: number,
    inputId: number,
    payload: TimflowSteadyRequest,
    days: number,
  ) => {
    setIsCalculatingTimflow(true);
    try {
      const response = await calculateTimflowSteady(routeCaptureId, inputId, payload, days);
      setTimflowResult(response);
    } catch (error) {
      const friendly = resolveMethodsInputError(
        error,
        captures,
        methodsInputsByCapture,
        t,
      );

      if (friendly) {
        enqueueSnackbar(friendly, {
          variant: "error",
          persist: true,
          action: (snackbarId) => (
            <IconButton
              color="inherit"
              size="small"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        });
      } else {
        handleError(error);
      }
    } finally {
      setIsCalculatingTimflow(false);
    }
  };

  const handleTimflowLoadResult = async (
    routeCaptureId: number,
    inputId: number,
  ) => {
    try {
      const response = await getCalculationByCaptureId(
        routeCaptureId,
        Method.TIMFLOW_STEADY,
      );

      const match = response.find(
        (r: any) => r.inputParameters?.wells?.[0]?.inputId === inputId,
      );
      setTimflowResult((match as unknown as TimflowSteadyResult) ?? null);
    } catch (error) {
      setTimflowResult(null);
      handleError(error);
    }
  };

  const handleTimflowCsv = async (
    routeCaptureId: number,
    inputId: number,
    payload: TimflowSteadyRequest,
    days: number,
  ) => {
    setIsDownloadingTimflow(true);
    try {
      await downloadTimflowSteadyCsv(routeCaptureId, inputId, payload, days);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadingTimflow(false);
    }
  };

  const handleCreateInputWithFlow = async (
    routeCaptureId: number,
    flow: number,
  ) => {
    const created = await createMethodsInputApi(routeCaptureId, {
      name: t("calculations.methods.timflow.quick-sheet-name"),
      flowM3Day: flow,
    } as CreateMethodsInputRequest);

    await queryClient.invalidateQueries({
      queryKey: ["methodsInputs", routeCaptureId],
    });

    return created;
  };

  const INPUT_VALUE_KEYS = [
    "name", "recharge", "hydraulicConductivity", "exploitationFlow", "testFlow",
    "flowM3Day", "flowM3Hour", "hydrostaticLevel", "hydrodynamicLevel",
    "transmissivity", "hydraulicGradient", "effectivePorosity", "storageCoefficient",
    "saturatedThickness", "saturatedThicknessTop", "saturatedThicknessBase", "aquiferType",
  ] as const;

  
  const handleSaveInputFlow = async (
    routeCaptureId: number,
    inputId: number,
    flow: number,
  ) => {
    const current = methodsInputsByCapture[routeCaptureId]?.find((mi) => mi.id === inputId);
    if (!current) throw new Error(`Input ${inputId} não encontrado`);

    const values = Object.fromEntries(
      INPUT_VALUE_KEYS.map((key) => [key, current[key]]),
    );

    await updateMethodsInput({ inputId, values: { ...values, flowM3Day: flow } });
    await queryClient.invalidateQueries({ queryKey: ["methodsInputs", routeCaptureId] });
  };

  if (isPendingInputs && subIndex === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderVulnerabilityMethod = (method: Method) => {
    const result = vulnerabilityResults[0] ?? null;
    const inputs = result?.inputParameters ?? null;

    switch (method) {
      case Method.DRASTIC_EPPNA:
        return (
          <CaptureDrasticEppna
            canEdit={canEdit}
            onCreate={async (values) => {
              await calculateVulnerability(values).catch(handleError);
            }}
            drasticEppnaInputs={inputs}
            drasticEppnaResult={result}
          />
        );
      case Method.GOD_AVI:
        return (
          <CaptureGodAvi
            captureId={Number(captureId)}
            canEdit={canEdit}
            lithologiesData={lithologiesData}
            onCreateLithology={async (values) => {
              await createLithology(values).catch(handleError);
            }}
            onUpdateLithology={async (id, values) => {
              await updateLithology({ lithologyId: id, values }).catch(
                handleError,
              );
            }}
            onDeleteLithology={async (id) => {
              await deleteLithology(id).catch(handleError);
            }}
            onCreate={async (values) => {
              await calculateVulnerability(values).catch(handleError);
            }}
            godAviInputs={inputs}
            godAviResult={result}
            onRequestRefresh={refetch}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {subIndex === 0 && (
        <Box>
          <MethodsInputs
            methodsInputsData={methodsInputsData}
            canEdit={canEdit}
            onCreate={async (values) => {
              await createMethodsInput(values).catch(handleError);
            }}
            onUpdate={async (id, values) => {
              await updateMethodsInput({ inputId: id, values }).catch(
                handleError,
              );
            }}
            onDelete={async (id) => {
              await deleteMethodsInput(id).catch(handleError);
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={methodsTabIndex}
              onChange={(_e, v) => setMethodsTabIndex(v)}
            >
              {FLOW_TEST_METHODS_CONFIG.map((method) => (
                <Tab key={method.method} label={t(method.labelKey)} />
              ))}
              <Tab
                key={Method.TIMFLOW_STEADY}
                label={t("calculations.methods.tabs.timflow-steady")}
              />
            </Tabs>
            {FLOW_TEST_METHODS_CONFIG.map((methodConfig, index) => (
              <Fade
                key={methodConfig.method}
                in={methodsTabIndex === index}
                unmountOnExit
              >
                <Box sx={{ mt: 2 }}>
                  {methodsTabIndex === index && (
                    <MethodRunner
                      captureId={Number(captureId)}
                      methodsInputs={methodsInputsData}
                      canEdit={canEdit}
                      config={methodConfig}
                      onUpdate={async (id, values) => {
                        await updateMethodsInput({ inputId: id, values }).catch(
                          handleError,
                        );
                      }}
                      onCreate={async (values) => {
                        return await createMethodsInput(values).catch((err) => {
                          handleError(err);
                          throw err;
                        });
                      }}
                    />
                  )}
                </Box>
              </Fade>
            ))}
            {methodsTabIndex === timflowTabIndex && (
              <Box sx={{ mt: 2 }}>
                <CaptureTimflowSteady
                  captureId={Number(captureId)}
                  canEdit={canEdit}
                  captures={captures}
                  methodsInputsByCapture={methodsInputsByCapture}
                  isLoadingInputs={isPendingTimflowInputs}
                  onWellCapturesChange={setTimflowCaptureIds}
                  onSaveInputFlow={handleSaveInputFlow}
                  onCreateInputWithFlow={handleCreateInputWithFlow}
                  result={timflowResult}
                  isCalculating={isCalculatingTimflow}
                  isDownloading={isDownloadingTimflow}
                  onCalculate={handleTimflowCalculate}
                  onDownloadCsv={handleTimflowCsv}
                  onLoadResult={handleTimflowLoadResult}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {subIndex === 1 && (
        <Box>
          <Tabs
            value={vulnerabilityTabIndex}
            onChange={(_e, v) => setVulnerabilityTabIndex(v)}
          >
            {VULNERABILITY_METHODS_CONFIG.map((method, index) => (
              <Tab
                key={method.method}
                label={t(method.labelKey)}
                value={index}
              />
            ))}
          </Tabs>
          {VULNERABILITY_METHODS_CONFIG.map((methodConfig, index) => (
            <Fade
              key={methodConfig.method}
              in={vulnerabilityTabIndex === index}
              unmountOnExit
            >
              <Box sx={{ mt: 2 }}>
                {isPendingVulnerability ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  vulnerabilityTabIndex === index &&
                  renderVulnerabilityMethod(methodConfig.method)
                )}
              </Box>
            </Fade>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CalculationsTab;